import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  Drawer,
  DrawerFooter,
  ErrorBanner,
  LoadingButton,
  useToast,
  type ImpactItem,
} from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { useCanOperar } from '@app/auth/useCan';
import { errorView, fieldMessage } from '@app/errors';
import { t, tn } from '@app/i18n';
import { useEnvironments } from '../TenantSettings/TenantSettings.api';
import { AgentDrawer } from './AgentDrawer';
import { ResourceForm } from './ResourceForm';
import { ToolsTab } from './ToolsTab';
import {
  useCreateResource,
  useDeleteResource,
  useDeleteResourceTool,
  useReimportResource,
  useResource,
  useResourceImpact,
  useResourceEnvironments,
  useUsersForOwner,
  useUpdateResource,
} from './Resources.api';
import {
  governedIs,
  manualIs,
  forAtualizacao,
  forCreation,
  resourceDefaults,
  textImpact,
  type ResourceFormValues,
} from './ResourceModel';
import { bannerManual, kind as kindBadge } from './resourceVocab';
import { useResourceValidation } from './useFormValidation';
import s from './Resources.module.css';
import { useSiteOptions } from '../Sites/Sites.api';

/**
 * T7 — drawer do resource (FR-029 a FR-035, FR-123, FR-124, FR-130, FR-131).
 *
 * Duas abas, como o protótipo (§3.2): **Geral** (o cadastro) e **Tools · {n}**
 * (a classificação). Salvar um resource do gateway publica o catálogo inteiro no
 * middleware e só grava depois do 2xx — por isso a falha vira banner com "nada
 * foi gravado" e o formulário mantém o que foi digitado (FR-033, FR-123).
 */

type TabID = 'geral' | 'tools';

/** O que a confirmação de exclusão está prestes a remover (FR-035). */
type Target = { tipo: 'resource' } | { tipo: 'tool'; toolId: string };

export interface ResourceDrawerProps {
  /** `null` = **Novo resource** (manual, R22). */
  resourceId: string | null;
  onClose: () => void;
}

/**
 * O drawer de T7 escolhe a forma pelo tipo (fase 2): um recurso agente não tem tools nem cadastro — o gateway é a
 * declaração —, e a gaveta dele é outra (`AgentDrawer`). A escolha mora num componente à parte porque os dois têm
 * hooks diferentes: decidir dentro de um deles mudaria a ordem dos hooks quando a leitura chega.
 */
export function ResourceDrawer({ resourceId, onClose }: ResourceDrawerProps) {
  const resource = useResource(resourceId);
  if (resourceId && resource.data?.kind === 'agent') {
    return <AgentDrawer resourceId={resourceId} onClose={onClose} />;
  }
  return <CatalogResourceDrawer resourceId={resourceId} onClose={onClose} />;
}

function CatalogResourceDrawer({ resourceId, onClose }: ResourceDrawerProps) {
  const criando = resourceId === null;
  const resource = useResource(resourceId);
  const toast = useToast();

  const canCreate = useCanOperar('createResource');
  const canSave = useCanOperar('updateResource');
  const canDelete = useCanOperar('deleteResource');
  const canReimportar = useCanOperar('reimportResource');
  const editavel = criando ? canCreate : canSave;
  // Opções do Dono (usuários) e do Ambiente (já usados): só quem edita precisa.
  const usuarios = useUsersForOwner(editavel);
  const ambientes = useResourceEnvironments(editavel);
  // Recursos usam metadado livre: incluir também cadastro não selecionável para identidade.
  const registeredEnvironments = useEnvironments();
  const environmentSuggestions = [...new Set([
    ...(registeredEnvironments.data?.items.map((item) => item.slug) ?? []),
    ...(ambientes.data ?? []),
  ])].sort((a, b) => a.localeCompare(b, 'pt-BR'));

  // Só a criação manual exige endpoint — é o que o contrato diz
  // (`ResourceCreate.required`; `ResourceUpdate.endpoint` é anulável).
  const validation = useResourceValidation({ exigeEndpoint: criando });
  const { control, handleSubmit, reset, watch, formState } = useForm<ResourceFormValues>({
    defaultValues: resourceDefaults(resource.data),
    ...validation,
  });

  // Sites do tenant para o seletor (P1, marco C). Vazio quando single-site — e é
  // isso que esconde o campo, num lugar só.
  const sitesDoTenant = useSiteOptions();

  const create = useCreateResource();
  const save = useUpdateResource(resourceId ?? '');
  const reimportar = useReimportResource(resourceId ?? '');
  const remove = useDeleteResource(resourceId ?? '');
  const removeTool = useDeleteResourceTool(resourceId ?? '');

  const [tab, setAba] = useState<TabID>('geral');
  /** Aviso do rodapé quando o Salvar para na validação (achado do E2E de T7). */
  const [avisoValidacao, setAvisoValidacao] = useState<string | null>(null);
  const [erroSalvar, setErroSalvar] = useState<ApiError | null>(null);
  const [erroReimportar, setErroReimportar] = useState<ApiError | null>(null);
  const [erroExcluir, setErroExcluir] = useState<ApiError | null>(null);
  const [alvo, setAlvo] = useState<Target | null>(null);

  const impacto = useResourceImpact(
    resourceId ?? '',
    alvo?.tipo === 'tool' ? alvo.toolId : null,
    alvo !== null && !criando,
  );

  // O resource chega depois do primeiro render: o formulário nasce com os
  // valores do servidor.
  useEffect(() => {
    if (resource.data) reset(resourceDefaults(resource.data));
  }, [resource.data, reset]);

  const tipo = watch('tipo');
  const tools = watch('tools');
  const gatewayID = watch('apim_id');
  const manual = criando || manualIs(resource.data);
  const importado = resource.data?.origin === 'apim';
  /**
   * Onde a tool pode nascer nesta tela (FR-027): só no recurso manual e na criação. Num recurso
   * importado as tools vêm do gateway — o `tools/list` pelo gateway num MCP, as operações numa
   * API —, e declarar à mão ali seria declarar o que o gateway lista. O MCP por policy antigo,
   * com tools declaradas aqui, saiu com o caminho antigo (aim-cpj, 08/10).
   */
  const governed = governedIs(resource.data);
  const declaravel = manual;
  const nome = resource.data?.name ?? resource.data?.gateway_id ?? t('resources.drawer.new');

  const send = handleSubmit(
    async (valores) => {
      setAvisoValidacao(null);
      setErroSalvar(null);
      try {
        if (criando) {
          const novo = await create.mutateAsync(forCreation(valores));
          reset(resourceDefaults(novo));
          toast.mostrar(
            t('resources.drawer.created', {
              name: novo.name,
              tools: tn('resources.drawer.toolsCount', novo.tools.length),
            }),
            { tone: 'sucesso' },
          );
          onClose();
          return;
        }
        const saved = await save.mutateAsync(forAtualizacao(valores, manual));
        reset(resourceDefaults(saved));
        toast.mostrar(
          t('resources.drawer.saved', {
            name: saved.name,
            tools: tn('resources.drawer.toolsCount', saved.tools.length),
          }),
          { tone: 'sucesso' },
        );
      } catch (erro) {
        const apiError = erro as ApiError;
        setErroSalvar(apiError);
        // `versao_antiga` descarta a tentativa e recarrega o registro (FR-124);
        // os demais códigos mantêm o que foi digitado (FR-123).
        if (errorView(apiError).efeito === 'recarregar' && !criando) {
          const current = await resource.refetch();
          if (current.data) reset(resourceDefaults(current.data));
        }
      }
    },
    /**
     * Validação reprovada. Sem isto o botão simplesmente não fazia nada quando
     * o campo com erro estava na **outra** aba: nenhuma requisição, nenhuma
     * mensagem à vista (achado do E2E de T7). Agora a tela vai para a aba do
     * primeiro campo com erro e o rodapé diz por que não enviou.
     */
    (erros) => {
      const campos = Object.keys(erros);
      if (campos.length === 0) return;
      const target: TabID = campos.every((campo) => campo === 'tools') ? 'tools' : 'geral';
      setAba(target);
      setAvisoValidacao(
        t('resources.drawer.validation.warning', {
          tab: t(target === 'tools' ? 'resources.drawer.tab.toolsName' : 'resources.drawer.tab.general'),
        }),
      );
    },
  );

  async function reimportarNow() {
    setErroReimportar(null);
    try {
      const updated = await reimportar.mutateAsync();
      reset(resourceDefaults(updated));
      toast.mostrar(t('resources.drawer.reimported', { name: updated.name }), {
        tone: 'sucesso',
      });
    } catch (erro) {
      setErroReimportar(erro as ApiError);
    }
  }

  async function confirmDeletion() {
    if (!alvo) return;
    setErroExcluir(null);
    try {
      if (alvo.tipo === 'resource') {
        await remove.mutateAsync();
        setAlvo(null);
        toast.mostrar(t('resources.drawer.removed', { name: nome }), { tone: 'sucesso' });
        onClose();
        return;
      }
      await removeTool.mutateAsync(alvo.toolId);
      setAlvo(null);
      const current = await resource.refetch();
      if (current.data) reset(resourceDefaults(current.data));
      toast.mostrar(t('resources.drawer.toolRemoved'), { tone: 'sucesso' });
    } catch (erro) {
      setErroExcluir(erro as ApiError);
    }
  }

  const wasRead = impacto.data;
  const impactItems: ImpactItem[] = [
    { label: t('resources.drawer.impact.capabilities'), count: wasRead?.capabilities ?? 0 },
    { label: t('resources.drawer.impact.contracts'), count: wasRead?.contracts ?? 0 },
    { label: t('resources.drawer.impact.policies'), count: wasRead?.policies ?? 0 },
  ];

  return (
    <Drawer
      open
      title={criando ? t('resources.drawer.new') : nome}
      subtitle={
        <span className={s.drawerBadges}>
          <Badge tone={kindBadge(tipo).tone}>{kindBadge(tipo).rotulo}</Badge>
          {importado ? (
            <Badge tone="neutral">{t('resources.drawer.badge.imported')}</Badge>
          ) : null}
          {manual && !criando ? (
            <Badge tone="warning">{t('resources.drawer.badge.outsideGateway')}</Badge>
          ) : null}
        </span>
      }
      dirty={formState.isDirty}
      onClose={onClose}
      footer={
        <DrawerFooter
          start={
            <>
              {!criando && canDelete ? (
                <Button variant="danger" icon="trash" onClick={() => setAlvo({ tipo: 'resource' })}>
                  {t('resources.drawer.footer.remove')}
                </Button>
              ) : null}
              {/* `role="status"`: o aviso nasce depois do clique, e quem usa
                leitor de tela precisa ouvir por que nada foi enviado. */}
              {avisoValidacao ? (
                <span className={s.avisoValidacao} role="status" data-testid="aviso-validacao">
                  {avisoValidacao}
                </span>
              ) : null}
            </>
          }
        >
          <Button variant="out" onClick={onClose}>
            {t('resources.drawer.footer.cancel')}
          </Button>
          {editavel ? (
            <LoadingButton
              variant="primary"
              loading={create.isPending || save.isPending}
              loadingLabel={t('resources.drawer.footer.save.loading')}
              onClick={() => void send()}
            >
              {t(criando ? 'resources.drawer.footer.create' : 'resources.drawer.footer.save')}
            </LoadingButton>
          ) : null}
        </DrawerFooter>
      }
    >
      {resource.isLoading ? <p className={s.carregando}>{t('resources.drawer.loading')}</p> : null}

      {resource.isError ? (
        <ErrorBanner
          error={resource.error}
          title={t('resources.drawer.error.open')}
          onRetry={() => void resource.refetch()}
          hideLink
        />
      ) : null}

      {registeredEnvironments.isError ? (
        <ErrorBanner error={registeredEnvironments.error}
          title={t('resources.form.environment.error')}
          onRetry={() => void registeredEnvironments.refetch()} hideLink />
      ) : null}
      {ambientes.isError ? (
        <ErrorBanner error={ambientes.error}
          title={t('resources.form.environment.error')}
          onRetry={() => void ambientes.refetch()} hideLink />
      ) : null}

      {/* FR-034: banner fixo — o PDP não impõe nada sobre um resource manual. */}
      {manual ? (
        <div data-testid="banner-manual">
          <Alert tone="warning" title={t('resources.drawer.manual.title')}>
            {bannerManual()}
          </Alert>
        </div>
      ) : null}

      {erroSalvar ? (
        <ErrorBanner
          error={erroSalvar}
          escrita
          title={t(criando ? 'resources.drawer.error.create' : 'resources.drawer.error.save')}
          onRetry={() => setErroSalvar(null)}
          retryLabel={t('resources.drawer.error.close')}
        >
          {erroSalvar.campos.length ? (
            <ul>
              {erroSalvar.campos.map((campo) => (
                <li key={campo.field}>{fieldMessage(campo)}</li>
              ))}
            </ul>
          ) : null}
        </ErrorBanner>
      ) : null}

      {erroReimportar ? (
        <ErrorBanner
          error={erroReimportar}
          title={t('resources.drawer.error.reimport')}
          onRetry={() => setErroReimportar(null)}
          retryLabel={t('resources.drawer.error.close')}
        />
      ) : null}

      {criando || resource.data ? (
        <>
          <div className={s.abas} role="tablist" aria-label={t('resources.drawer.tabs.label')}>
            <button
              type="button"
              role="tab"
              data-testid="aba-geral"
              aria-selected={tab === 'geral'}
              className={[s.aba, tab === 'geral' ? s.abaOn : ''].filter(Boolean).join(' ')}
              onClick={() => setAba('geral')}
            >
              {t('resources.drawer.tab.general')}
            </button>
            <button
              type="button"
              role="tab"
              data-testid="aba-tools"
              data-tools={tools.length}
              aria-selected={tab === 'tools'}
              className={[s.aba, tab === 'tools' ? s.abaOn : ''].filter(Boolean).join(' ')}
              onClick={() => setAba('tools')}
            >
              {t('resources.drawer.tab.tools', { n: tools.length })}
            </button>
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              void send();
            }}
          >
            {tab === 'geral' ? (
              <ResourceForm
                resource={resource.data}
                control={control}
                errors={formState.errors}
                usuarios={usuarios.data ?? []}
                ambientes={environmentSuggestions}
                editavel={editavel}
                tipo={tipo}
                // Lista vazia sem `multi`: o campo nem aparece no tenant de um site.
                sites={sitesDoTenant}
              />
            ) : (
              <ToolsTab
                control={control}
                errors={formState.errors}
                tools={tools}
                apimId={gatewayID}
                tipo={tipo}
                manual={manual}
                importado={Boolean(importado)}
                governed={governed}
                declaravel={declaravel}
                editavel={editavel}
                reimportando={reimportar.isPending}
                onReimportar={canReimportar ? () => void reimportarNow() : undefined}
                onRemoverGravada={(toolId) => setAlvo({ tipo: 'tool', toolId })}
              />
            )}
          </form>
        </>
      ) : null}

      <ConfirmDialog
        open={alvo !== null}
        destructive
        title={
          alvo?.tipo === 'tool'
            ? t('resources.drawer.remove.toolTitle')
            : t('resources.drawer.remove.resourceTitle', { name: nome })
        }
        message={
          wasRead ? (
            textImpact(
              alvo?.tipo === 'tool' ? 'tool' : 'resource',
              wasRead.capabilities,
              wasRead.contracts,
              wasRead.policies,
            )
          ) : impacto.isError ? (
            // Sem impacto lido não há confirmação informada: o FR-130 manda
            // *listar* as contagens, e o ramo binário anterior (`isLoading ? … :
            // textoImpacto(0,0,0)`) escrevia "0 capabilities de 0 contratos" sobre
            // uma leitura que falhou — com o botão vermelho habilitado. Zero lido e
            // zero por não ter lido são coisas diferentes, e a frase não as
            // distinguia. Mesmo desenho do drawer de identidade (FR-056).
            <p>{t('resources.drawer.remove.impactRetry')}</p>
          ) : (
            <p>{t('resources.drawer.remove.impactLoading')}</p>
          )
        }
        impact={wasRead ? impactItems : undefined}
        confirmLabel={t(
          alvo?.tipo === 'tool'
            ? 'resources.drawer.remove.confirmTool'
            : 'resources.drawer.remove.confirmResource',
        )}
        loading={remove.isPending || removeTool.isPending}
        confirmDisabled={!wasRead}
        error={
          erroExcluir ? (
            <ErrorBanner
              error={erroExcluir}
              escrita
              title={t('resources.drawer.remove.error')}
            />
          ) : impacto.isError ? (
            <ErrorBanner
              error={impacto.error}
              title={t('resources.drawer.remove.impactError')}
              onRetry={() => void impacto.refetch()}
              hideLink
            />
          ) : null
        }
        onConfirm={() => void confirmDeletion()}
        onCancel={() => {
          setAlvo(null);
          setErroExcluir(null);
        }}
      />
    </Drawer>
  );
}
